/**
 * The `permanent` filter set for `/tina/venue-audit`.
 *
 * Every entry is a real `GET /venue-audit/findings` param — `status`, `kind`, `severity` —
 * so the whole page is server-side: the rows, the `total` the pagination footer reads, and
 * the narrowing. All three are typed as Literals on the route, so a value this build
 * invented would 422 rather than quietly return an unfiltered page.
 *
 * **`status` is always a real value, never undefined.** The route defaults it to `open`,
 * but sending it explicitly means a restored or shared URL can never render a page the
 * curator believes is filtered while it is not.
 *
 * **`kind` and `severity` are always PRESENT, carrying `value: undefined` when cleared** —
 * the same always-emit convention `buildRefreshReviewFilters` and `buildItemsFilters`
 * follow, and for the same reason. Refine builds the query as
 * `unionFilters(permanent, internalState)` and drops undefined entries *after* the dedupe,
 * so a present-but-undefined entry shadows a stale internal copy while an omitted one lets
 * it survive; with `syncWithLocation` on, that stale copy is whatever the previous URL
 * carried, which is how a table gets stuck on a filter the curator cleared. The data
 * provider's `flattenFilters` then drops the undefined value, so nothing reaches the wire.
 */
import type { CrudFilters } from "@refinedev/core";

import type {
  VenueAuditKind,
  VenueAuditSeverity,
  VenueAuditStatus,
} from "@/types/tina";

export interface VenueAuditFilterState {
  /** Which view of the queue: open, dismissed, resolved, or everything. */
  status: VenueAuditStatus;
  /** The toolbar's kind select; `null` is "any kind", which sends no `kind` at all. */
  kind: VenueAuditKind | null;
  /** The toolbar's severity select; `null` is "any severity". */
  severity: VenueAuditSeverity | null;
}

export const buildVenueAuditFilters = ({
  status,
  kind,
  severity,
}: VenueAuditFilterState): CrudFilters =>
  [
    { field: "status", operator: "eq", value: status },
    { field: "kind", operator: "eq", value: kind ?? undefined },
    { field: "severity", operator: "eq", value: severity ?? undefined },
  ] as CrudFilters;
